import { useCallback, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import Group from 'components/utils/Group'
import { rem } from 'polished'

// Desktop Mega Menu Button Styles
const ButtonContainer = styled.div<{ $relative?: boolean }>`
  position: ${props => props.$relative ? 'relative' : 'static'};
  display: inline-block;
`

const StyledMenuButton = styled(TextButton)`
  && {
    height: ${rem(48)};
    padding: ${rem(0)} ${rem(10)};
    text-decoration: none;

    span {
      border-bottom: none;
    }
  }
`

interface MegaMenuButtonProps {
  label: string
  to?: string
  onClick?: () => void
  onOpenChange?: (open: boolean) => void
  alignDropdownWithButton?: boolean
  showChevron?: boolean
  children?: (menuOpen: boolean, onClose: () => void) => React.ReactNode
}

export function MerchandiseNavBarButton(props: MegaMenuButtonProps) {
  const {
    label,
    to,
    onClick,
    onOpenChange,
    alignDropdownWithButton = false,
    showChevron = true,
    children,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const handleMouseEnter = useCallback(() => {
    if (!children) return
    setMenuOpen(true)
    onOpenChange?.(true)
  }, [onOpenChange, children])

  const handleMouseLeave = useCallback(() => {
    if (!children) return
    setMenuOpen(false)
    onOpenChange?.(false)
  }, [onOpenChange, children])

  const handleClose = useCallback(() => {
    setMenuOpen(false)
    onOpenChange?.(false)
  }, [onOpenChange])

  return <ButtonContainer
    ref={containerRef}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
    $relative={alignDropdownWithButton}
  >
    <StyledMenuButton
      kind="tertiary"
      variant="default"
      size="medium"
      to={to}
      onClick={onClick}
    >
      <Group direction="horizontal" gap={4} verticalAlign="center">
        {label}
        {showChevron && <LineAngleDownIcon size={16} colour="neutral-two" />}
      </Group>
    </StyledMenuButton>
    {children?.(menuOpen, handleClose)}
  </ButtonContainer>
}

interface FilterChipProps {
  label: string
  to?: string
  showChevron?: boolean
  onClick?: () => void
}

export function MerchandiseNavBarFilterChip(props: FilterChipProps) {
  const { label, to, showChevron = false, onClick } = props

  return (
    <AssistChip
      variant="filled"
      size="small"
      to={to}
      onClick={onClick}
    >
      <Group direction="horizontal" gap={4} verticalAlign="center">
        {label}
        {showChevron && <LineAngleDownIcon size={16} colour="neutral-one" />}
      </Group>
    </AssistChip>
  )
}

// Default export for backward compatibility
export default MerchandiseNavBarButton
